/**
 * The browser half of authentication.
 *
 * Replaces the Supabase client. Two pieces of state, and where each lives is
 * the whole design:
 *
 *   - The **access token** is held in a module variable and nowhere else. Not
 *     localStorage, not sessionStorage. It is short-lived, and keeping it out
 *     of storage means an XSS cannot walk off with a durable credential.
 *   - The **refresh token** is an httpOnly cookie this file cannot read at all.
 *     The server sets it; the browser returns it; script never touches it.
 *
 * A page reload therefore starts with no token and calls `bootstrap()`, which
 * is one round trip — the same cost the old `/api/config` fetch imposed before
 * anything could render.
 */

/** Refresh this long before the token actually expires, to avoid a 401 race. */
const REFRESH_MARGIN_MS = 60_000;

let accessToken = null;
let expiresAt = 0;
let refreshPromise = null;

const listeners = new Set();

/** Fires only on a real transition, never on a silent rotation. */
function setToken(token, expiresInSeconds) {
  const had = Boolean(accessToken);
  accessToken = token;
  expiresAt = token ? Date.now() + expiresInSeconds * 1000 : 0;

  const has = Boolean(accessToken);
  if (had !== has) {
    for (const listener of listeners) listener(accessToken);
  }
}

export function onAuthChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function clearSession() {
  setToken(null, 0);
}

/** The token as it stands, without triggering a refresh. */
export function currentToken() {
  return accessToken;
}

async function post(path, body) {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    // The refresh cookie has to travel, and it is same-origin either way.
    credentials: 'same-origin',
    body: JSON.stringify(body ?? {}),
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error(payload?.error || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }

  return payload;
}

/**
 * Exchange the refresh cookie for a new access token.
 *
 * Single flight. Ten queries can 401 at the same moment; without this they
 * would fire ten rotations, and the server would rightly treat nine of them as
 * a replayed token and revoke the whole session.
 */
export function refresh() {
  if (!refreshPromise) {
    refreshPromise = post('/api/auth/refresh')
      .then((result) => {
        setToken(result.access_token, result.expires_in);
        return true;
      })
      .catch((error) => {
        // Only the server saying no ends the session. A dropped connection or
        // a deploy mid-restart (no response, or a 5xx) says nothing about the
        // cookie — it is still good, and signing out over it threw away
        // whatever was half-typed.
        if (isSessionOver(error)) clearSession();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

/**
 * The token to put on the next request.
 *
 * Refreshes ahead of expiry rather than waiting for a 401, so the retry path is
 * the exception rather than the rule.
 */
export async function getAccessToken() {
  if (!accessToken) {
    // No token at all means signed out — the refresh cookie is httpOnly, so
    // there is nothing here to inspect and guess with. `bootstrap()` covers a
    // fresh page load; anything else falls through to the 401 retry in api.js.
    return null;
  }

  if (Date.now() < expiresAt - REFRESH_MARGIN_MS) return accessToken;

  try {
    await refresh();
  } catch {
    // Rejected: the session is cleared and this is null. Unreachable: send
    // what we have, and let the request itself report the outage.
    return accessToken;
  }

  return accessToken;
}

/** True when a failed refresh means the session is over, not just unreachable. */
export function isSessionOver(error) {
  return error?.status === 401;
}

/**
 * On page load: is there still a live session behind the cookie?
 *
 * Resolves false only when the server says no. If the server cannot be
 * reached it rejects, so a reload during a deploy says "not reachable"
 * rather than asking someone who is signed in to sign in again.
 */
export async function bootstrap() {
  try {
    await refresh();
    return true;
  } catch (error) {
    if (isSessionOver(error)) return false;
    throw error;
  }
}

export async function signIn(email, password) {
  const result = await post('/api/auth/sign-in', { email, password });
  setToken(result.access_token, result.expires_in);
}

export async function signOut() {
  try {
    await post('/api/auth/sign-out');
  } catch {
    // Already gone as far as the server is concerned. Clearing locally is
    // still the right outcome.
  }
  clearSession();
}

export async function requestPasswordReset(email) {
  await post('/api/auth/request-password-reset', { email });
}

export async function acceptInvite(token, password) {
  const result = await post('/api/auth/accept-invite', { token, password });
  setToken(result.access_token, result.expires_in);
}

export async function resetPassword(token, password) {
  const result = await post('/api/auth/reset-password', { token, password });
  setToken(result.access_token, result.expires_in);
}

/** What an invitation or reset link is for, before anyone types a password. */
export async function describeInvite(token) {
  const response = await fetch(`/api/auth/invite/${encodeURIComponent(token)}`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) return null;
  return response.json().catch(() => null);
}
